import yaml from 'js-yaml'
import { stripEmoji } from './lessonRenderer'

/**
 * Parse YAML frontmatter from a markdown string.
 * Browser-safe — no gray-matter, no Node Buffer dependency.
 * Frontmatter format: ---\nkey: value\n---\n
 */
function parseFrontmatter(raw) {
  // Normalize line endings up front so the `\n---` delimiter slicing
  // works regardless of whether the file was edited on Windows
  // (\r\n) or Unix (\n). Without this, files saved by some editors
  // get the body sliced one byte short and leave a stray \r at the
  // start of the rendered content.
  const str = (typeof raw === 'string' ? raw : String(raw)).replace(/\r\n/g, '\n')

  // Must start with ---
  if (!str.startsWith('---')) {
    return { data: {}, content: str }
  }

  const end = str.indexOf('\n---', 3)
  if (end === -1) {
    return { data: {}, content: str }
  }

  const yamlStr  = str.slice(3, end).trim()
  const content  = str.slice(end + 4).trim() // skip \n---

  let data = {}
  try {
    data = yaml.load(yamlStr) || {}
  } catch (e) {
    console.warn('Frontmatter parse error:', e.message)
  }

  return { data, content }
}

/**
 * Parse a raw lesson markdown string into structured data.
 * Returns { meta, body, quiz }
 */
export function parseLesson(rawContent) {
  const raw = typeof rawContent === 'string' ? rawContent : String(rawContent)
  const { data: meta, content: body } = parseFrontmatter(raw)

  // Strip HTML comments (template instructions)
  const bodyClean = body.replace(/<!--[\s\S]*?-->/g, '').trim()

  // Extract fenced ```quiz block
  const quizMatch = bodyClean.match(/```quiz\n([\s\S]*?)```/)
  let quiz = null
  let bodyWithoutQuiz = bodyClean

  if (quizMatch) {
    try {
      const rawQuiz = yaml.load(quizMatch[1])
      // Strip emojis from all quiz text fields
      if (rawQuiz?.questions) {
        rawQuiz.questions = rawQuiz.questions.map(q => ({
          ...q,
          text: stripEmoji(q.text || ''),
          options: (q.options || []).map(o => ({
            ...o,
            text: stripEmoji(o.text || ''),
            explanation: stripEmoji(o.explanation || ''),
          }))
        }))
      }
      quiz = rawQuiz
    } catch (e) {
      console.error('Quiz parse error in lesson', meta?.id, e.message)
    }
    bodyWithoutQuiz = bodyClean.replace(quizMatch[0], '').trim()
  }

  return { meta, body: bodyWithoutQuiz, quiz }
}

/**
 * Get next and previous lessons relative to current slug.
 * Index should be pre-filtered to exclude intros/refs.
 */
export function getAdjacentLessons(index, currentSlug) {
  const idx = index.findIndex(l => l.slug === currentSlug)
  return {
    prev: idx > 0 ? index[idx - 1] : null,
    next: idx < index.length - 1 ? index[idx + 1] : null,
  }
}
